/**
 * @file vehicle-route-card.tsx
 * @module app/modules/orders-delivery/components/delivery-plan/vehicle-route-card
 * @description Per-vehicle route card for the delivery plan details screen: header
 * with edit/generate/view-map actions and duration/weight badges, expandable order
 * table, and the route map modal for this vehicle's stops.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.2.1
 * @version 2.1.2
 */

import { Button, Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import clsx from 'clsx';
import Method from '../../../../../utils/methods';
import { IMAGES } from '../../../../../utils/assetsAndPermissions';
import GeocodeAddress from '../geo/geocode-address';
import RouteMap from '../route-map';
import { kgToTons } from '../../utils/delivery-plan-details-helpers';
import { formatDeliveryAddress } from 'src/app/modules/_common/utils/address';

interface VehicleRouteCardProps {
  vehicleVal: any;
  vehicleIndex: number;
  selectedVehicle: any[];
  isPlanCreate: boolean;
  showRouteDetails: boolean;
  coordinates: any[];
  onHideRouteMap: () => void;
  onEditRouteOrders: (vehicleVal: any, vehicleIndex: number) => void;
  onGenerateOrAddOrder: (vehicleVal: any) => void;
  onViewMap: (vehicleVal: any) => void;
  onToggleOpen: (vehicleIndex: number) => void;
}

const renderEditDisabledTooltip = (props: any) => (
  <Tooltip className="tooltip w-lg-275px" {...props}>
    <p className="text-white mb-0 fs-14 fw-500">
      Edit route order is disabled due to partial orders in truck. Generate new plan
      to modify vehicle orders from review list
    </p>
  </Tooltip>
);

const VehicleRouteCard = ({
  vehicleVal,
  vehicleIndex,
  selectedVehicle,
  isPlanCreate,
  showRouteDetails,
  coordinates,
  onHideRouteMap,
  onEditRouteOrders,
  onGenerateOrAddOrder,
  onViewMap,
  onToggleOpen,
}: VehicleRouteCardProps) => {
  const hasPartialOrder = vehicleVal.orders.some(
    (orderVal: any) => orderVal.partialDelivery == true,
  );

  return (
    <Col xs={12}>
      {showRouteDetails && coordinates.length ? (
        <RouteMap show={showRouteDetails} onHide={onHideRouteMap} coordinates={coordinates} />
      ) : (
        <></>
      )}
      <Card className="p-0 bg-light">
        <Card.Header
          className={clsx('border', vehicleVal.isOpen ? '' : 'border-r10px')}
        >
          <Row className="fs-16 fw-600 align-items-center w-100">
            <Col md className="mb-md-0 mb-5 text-start">
              <Button className="btn-flush btn-white w-100 text-start" variant="">
                {vehicleVal.postData.dModel.name ? (
                  <span className={clsx('fw-bolder text-dark')}>
                    {vehicleVal.postData.dModel.name}{' '}/{' '}
                    {vehicleVal.postData.vModel.name +
                      '-' +
                      vehicleVal.postData.vModel.number}
                  </span>
                ) : (
                  <></>
                )}{' '}
              </Button>
            </Col>
            <Col md="auto">
              {hasPartialOrder ? (
                <OverlayTrigger placement="bottom" trigger="hover" overlay={renderEditDisabledTooltip}>
                  <Button className="uw-btn uw-btn-primary">{'Edit Route Orders'}</Button>
                </OverlayTrigger>
              ) : (
                <Button
                  className="uw-btn uw-btn-primary"
                  onClick={() => onEditRouteOrders(vehicleVal, vehicleIndex)}
                  disabled={
                    selectedVehicle.includes(vehicleVal.postData.vModel.reference) ||
                    hasPartialOrder
                  }
                >
                  {'Edit Route Orders'}
                </Button>
              )}{' '}
            </Col>
            <Col md="auto">
              <Button
                className="uw-btn uw-btn-primary"
                disabled={
                  isPlanCreate ||
                  selectedVehicle.includes(vehicleVal.postData.vModel.reference)
                }
                onClick={() => onGenerateOrAddOrder(vehicleVal)}
              >
                {selectedVehicle.includes(vehicleVal.postData.vModel.reference)
                  ? 'Plan generated'
                  : 'Generate Plan'}{' '}
              </Button>
            </Col>
            <Col md="auto">
              <Button className="uw-btn uw-btn-primary" onClick={() => onViewMap(vehicleVal)}>
                View Map
              </Button>
            </Col>
            <Col md="auto">
              <div className="d-flex flex-row">
                <div className="d-flex align-items-center">
                  {vehicleVal.orders.reduce((p: any, c: any) => p + c.duration, 0) >
                  vehicleVal.vehicleDurationLimit ? (
                    <span className="badge badge-danger fs-15 fw-600 text-white p-3 border-r4px me-5">
                      {Method.decimalHoursToHoursMinutesColon(
                        vehicleVal.orders.reduce((p: any, c: any) => p + c.duration, 0),
                      )}
                      /{' '}
                      {Method.decimalHoursToHoursMinutesColon(
                        vehicleVal.vehicleDurationLimit,
                      )}
                    </span>
                  ) : (
                    <span className="badge badge-light-warning fs-15 fw-600 text-dark p-3 border-r4px me-5">
                      {Method.decimalHoursToHoursMinutesColon(
                        vehicleVal.orders.reduce((p: any, c: any) => p + c.duration, 0),
                      )}
                      /{' '}
                      {Method.decimalHoursToHoursMinutesColon(
                        vehicleVal.vehicleDurationLimit,
                      )}
                    </span>
                  )}
                </div>
                <div className="d-flex align-items-center">
                  <span className="badge badge-light-warning fs-15 fw-600 text-dark p-3 border-r4px me-5">
                    {kgToTons(
                      vehicleVal.orders.reduce(
                        (orderTotal: any, order: any) =>
                          orderTotal +
                          order.processedVariants.reduce(
                            (variantTotal: any, variant: any) =>
                              variantTotal +
                              variant.dimensions.weight * variant.stockCount,
                            0,
                          ),
                        0,
                      ),
                    )}
                    /{' '}
                    {kgToTons(vehicleVal.vehicleCapacity)}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  {vehicleVal.isOpen ? (
                    <Button
                      variant="link"
                      className="btn-flush"
                      onClick={() => onToggleOpen(vehicleIndex)}
                    >
                      <img src={IMAGES.ArrowUp} alt="" />
                    </Button>
                  ) : (
                    <Button
                      variant="link"
                      className="btn-flush"
                      onClick={() => onToggleOpen(vehicleIndex)}
                    >
                      <img src={IMAGES.ArrowDown} alt="" />
                    </Button>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Card.Header>
        {vehicleVal.isOpen ? (
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-rounded table-row-bordered align-middle gs-9 gy-4 mb-0">
                <thead>
                  <tr className="fs-16 fw-600">
                    <th className="col-w-10">Order No</th>
                    <th className="col-w-30">Shop Name/Address</th>
                    <th className="col-w-30">Google Address</th>
                    <th className="col-w-10">Duration</th>
                    <th className="col-w-10">Distance</th>
                    <th className="col-w-10 text-end">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicleVal.orders.map((val: any) => {
                    return (
                      <tr>
                        <td>
                          {val.partialDelivery ? (
                            <div className="d-flex align-items-start">
                              <div className="d-flex flex-row">
                                {val.partialDelivery ? (
                                  <>
                                    <img
                                      className="me-2"
                                      width={28}
                                      height={21}
                                      src={IMAGES.PartialTruckTwo}
                                      alt=""
                                    />
                                  </>
                                ) : (
                                  <></>
                                )}
                              </div>
                              <span className=" fs-15 fw-500">#{val.refKey}</span>
                            </div>
                          ) : (
                            <>
                              <span className=" fs-15 fw-500">#{val.refKey}</span>
                            </>
                          )}
                        </td>{' '}
                        <td>
                          <span className="fs-15 fw-500">
                            {formatDeliveryAddress(val.address)}
                          </span>
                        </td>
                        <td>
                          <GeocodeAddress lat={val.lat} lng={val.lng} />
                          <br />
                          <span className="fs-15 fw-500 text-gray">
                            {`Latitude: ${val.lat}, Longitude: ${val.lng}`}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500">
                            {Method.decimalHoursToHoursMinutes(val.duration)}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500">
                            {Method.metersToKilometersMeters(val.distance)}
                          </span>
                        </td>
                        <td className="text-end">
                          <span className="fs-15 fw-500">
                            {kgToTons(
                              val.processedVariants.reduce(
                                (variantTotal: any, variant: any) =>
                                  variantTotal +
                                  variant.dimensions.weight * variant.stockCount,
                                0,
                              ),
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card.Body>
        ) : (
          <></>
        )}
      </Card>
    </Col>
  );
};

export default VehicleRouteCard;
